import { motion } from "framer-motion";
import { useTheme } from "next-themes";

/**
 * HeroBlobs — two overlapping ambient blobs behind the hero.
 *
 * Anti-banding strategy:
 *   • Moderate blur (~220px) rather than extreme (400px+) → gradients render
 *     with smoother transitions instead of visible rings.
 *   • Densely-stopped multi-radial fields → many intermediate colour stops
 *     mean no single big alpha jump for the browser to quantise into rings.
 *   • `transform: translateZ(0)` forces GPU compositing which uses higher
 *     precision colour interpolation.
 *   • Radial gradients reach exactly `transparent 78%` (not extreme 96%)
 *     so the natural blur handles edge fade — no gradient edge to visibly
 *     round off.
 */
export function HeroBlobs() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const yellowField = isDark
    ? `
        radial-gradient(ellipse 55% 55% at 46% 42%, rgba(247,208,72,0.52) 0%, rgba(247,208,72,0.38) 24%, rgba(247,208,72,0.22) 52%, rgba(247,208,72,0.08) 74%, transparent 92%),
        radial-gradient(ellipse 38% 38% at 62% 32%, rgba(247,208,72,0.36) 0%, rgba(247,208,72,0.18) 46%, transparent 80%),
        radial-gradient(ellipse 34% 34% at 34% 60%, rgba(247,208,72,0.32) 0%, rgba(247,208,72,0.16) 46%, transparent 80%)
      `
    : `
        radial-gradient(ellipse 55% 55% at 46% 42%, rgba(247,208,72,0.62) 0%, rgba(247,208,72,0.46) 24%, rgba(247,208,72,0.28) 52%, rgba(247,208,72,0.10) 74%, transparent 92%),
        radial-gradient(ellipse 38% 38% at 62% 32%, rgba(247,208,72,0.44) 0%, rgba(247,208,72,0.22) 46%, transparent 80%),
        radial-gradient(ellipse 34% 34% at 34% 60%, rgba(247,208,72,0.40) 0%, rgba(247,208,72,0.20) 46%, transparent 80%)
      `;

  // Dark-mode navy uses a slightly-lifted hue so it's visible against ink.
  const navyField = isDark
    ? `
        radial-gradient(ellipse 55% 55% at 46% 46%, rgba(80,96,138,0.42) 0%, rgba(80,96,138,0.30) 24%, rgba(80,96,138,0.18) 52%, rgba(80,96,138,0.06) 74%, transparent 92%),
        radial-gradient(ellipse 36% 36% at 34% 34%, rgba(80,96,138,0.28) 0%, rgba(80,96,138,0.14) 46%, transparent 80%),
        radial-gradient(ellipse 30% 30% at 62% 40%, rgba(80,96,138,0.24) 0%, rgba(80,96,138,0.12) 46%, transparent 80%)
      `
    : `
        radial-gradient(ellipse 55% 55% at 46% 46%, rgba(26,34,51,0.36) 0%, rgba(26,34,51,0.26) 24%, rgba(26,34,51,0.16) 52%, rgba(26,34,51,0.06) 74%, transparent 92%),
        radial-gradient(ellipse 36% 36% at 34% 34%, rgba(26,34,51,0.26) 0%, rgba(26,34,51,0.14) 46%, transparent 80%),
        radial-gradient(ellipse 30% 30% at 62% 40%, rgba(26,34,51,0.22) 0%, rgba(26,34,51,0.12) 46%, transparent 80%)
      `;

  const blur = "blur(220px)";

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[130vh] overflow-hidden"
    >
      {/* NAVY — below-left, sits behind */}
      <motion.div
        className="absolute"
        style={{
          right: "18%",
          top: "22%",
          width: "min(60vw, 780px)",
          height: "min(60vw, 780px)",
          background: navyField,
          filter: blur,
          transform: "translateZ(0)",
          willChange: "transform, filter",
        }}
        animate={{
          x: [0, -18, 12, 0],
          y: [0, 14, -10, 0],
          scale: [1, 1.05, 0.97, 1],
        }}
        transition={{ duration: 32, repeat: Infinity, ease: "easeInOut" }}
      />
      {/* YELLOW — upper-right, front */}
      <motion.div
        className="absolute"
        style={{
          right: "-4%",
          top: "-4%",
          width: "min(58vw, 760px)",
          height: "min(58vw, 760px)",
          background: yellowField,
          filter: blur,
          transform: "translateZ(0)",
          willChange: "transform, filter",
        }}
        animate={{
          x: [0, 16, -10, 0],
          y: [0, -12, 8, 0],
          scale: [1, 1.04, 0.98, 1],
        }}
        transition={{ duration: 28, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>
  );
}
